import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { API_KEY_COPY_SUCCESS, NgxToastrService } from '@app/_shared';
import { MIXPANEL_EVENTS } from '@app/_shared/providers/mixpanel/mixpanel.events';
import { MixpanelService } from '@app/_shared/providers/mixpanel/mixpanel.service';
import { ALGO_SPACE_MAPPING_FROZEN_WHILE_LOGGED_IN } from '@app/_shared/strings';
import { forkJoin, of } from 'rxjs';
import { AlgoSpace, AlgoSpaceMapping, MapAlgoSpaceDialogData } from '../manage-broker.interface';
import { ManageBrokerService } from '../manage-broker.service';

/**
 * Assign / unassign algo spaces for a broker connection.
 * Supports select-only mode (broker setup) and read-only when logged in with static IP (UT-9281).
 */
@Component({
  selector: 'app-map-algo-space',
  templateUrl: './map-algo-space.component.html',
  styleUrls: ['./map-algo-space.component.scss']
})
export class MapAlgoSpaceComponent implements OnInit {
  algoSpaces: AlgoSpace[] = [];
  selectedSpaceIds = new Set<string>();
  isLoading = true;
  isRefreshing = false;
  isSubmitting = false;
  brokerHasRunningAlgos = false;
  brokerDisplayName = '';
  brokerImageLink = '/assets/utradeSymbol.svg';
  readonly frozenTooltip = ALGO_SPACE_MAPPING_FROZEN_WHILE_LOGGED_IN;

  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: MapAlgoSpaceDialogData,
    private dialogRef: MatDialogRef<MapAlgoSpaceComponent>,
    private snackBar: NgxToastrService,
    private manageBrokerService: ManageBrokerService,
    private mixpanel: MixpanelService,
    private clipboard: Clipboard,
  ) {}

  ngOnInit(): void {
    const broker = this.data.brokerDetails;
    const brokerName = broker?.broker ?? this.data.brokerName ?? '';
    const brokerInfo = brokerName ? this.manageBrokerService.cachedAvailableBrokers?.[brokerName] : undefined;
    this.brokerDisplayName = this.data.brokerDisplayName ?? brokerInfo?.display_name ?? brokerName;
    this.brokerImageLink = this.data.brokerImageLink ?? brokerInfo?.image_link ?? '/assets/utradeSymbol.svg';

    if (this.data.selectOnly) {
      this.data.initialSelectedSpaceIds?.forEach((id) => this.selectedSpaceIds.add(id));
    } else {
      broker?.algo_spaces?.forEach((space) => this.selectedSpaceIds.add(space.id));
    }

    this.loadAlgoSpaceContext();
    this.fireMixpanelEvent(MIXPANEL_EVENTS.VIRTUAL_ALGO_SPACE_DASHBOARD, {
      from: this.data.from,
    });
  }

  get isSelectOnly(): boolean {
    return !!this.data.selectOnly;
  }

  get isReadOnly(): boolean {
    if (this.isSelectOnly || !this.data.brokerDetails) {
      return false;
    }
    return this.manageBrokerService.isAlgoSpaceMappingFrozen(this.data.brokerDetails);
  }

  get dialogTitle(): string {
    return this.isSelectOnly ? 'Choose Algo Spaces' : 'Assign Algo Spaces';
  }

  get brokerName(): string {
    return this.data.brokerDetails?.broker ?? this.data.brokerName ?? '';
  }

  connectionMaxSpaceMappings: number | null = null;

  get maxAllowedMapping(): number | null {
    if (this.connectionMaxSpaceMappings != null) {
      return this.connectionMaxSpaceMappings;
    }
    return this.brokerName ? this.manageBrokerService.getMaxAllowedMapping(this.brokerName) : null;
  }

  refreshAlgoSpaces(): void {
    if (this.isLoading || this.isRefreshing || this.isSubmitting || this.isReadOnly) {
      return;
    }
    this.loadAlgoSpaceContext(true);
  }

  getMapBlockReason(space: AlgoSpace, wantsMapped: boolean): string | null {
    if (this.isReadOnly) {
      return this.frozenTooltip;
    }
    const statusBlockReason = this.manageBrokerService.getAlgoSpaceStatusBlockReason(space, wantsMapped);
    if (statusBlockReason) {
      return statusBlockReason;
    }
    if (this.data.brokerDetails) {
      const reason = this.manageBrokerService.getAlgoSpaceMapBlockReason(
        space,
        this.data.brokerDetails,
        wantsMapped,
        this.brokerHasRunningAlgos,
      );
      if (reason) {
        return reason;
      }
    }
    if (wantsMapped && !this.selectedSpaceIds.has(space.id)) {
      return this.manageBrokerService.getAlgoSpaceMaxMappingBlockReason(
        this.brokerName,
        this.selectedSpaceIds.size,
        true,
      );
    }
    return null;
  }

  getSpaceDisabledTooltip(space: AlgoSpace): string {
    const isSelected = this.selectedSpaceIds.has(space.id);
    return this.getMapBlockReason(space, !isSelected) ?? '';
  }

  isSpaceInteractionDisabled(space: AlgoSpace): boolean {
    if (this.isReadOnly) {
      return true;
    }
    const isSelected = this.selectedSpaceIds.has(space.id);
    return !!this.getMapBlockReason(space, !isSelected);
  }

  toggleSpace(space: AlgoSpace, checked?: boolean): void {
    const isSelected = this.selectedSpaceIds.has(space.id);
    const wantsMapped = checked ?? !isSelected;
    const blockReason = this.getMapBlockReason(space, wantsMapped);
    if (blockReason) {
      if (wantsMapped && blockReason) {
        this.snackBar.simpleCustomToaster(blockReason, false);
      }
      return;
    }
    if (wantsMapped) {
      if (this.maxAllowedMapping === 1) {
        this.selectedSpaceIds = new Set([space.id]);
        return;
      }
      this.selectedSpaceIds = new Set([...this.selectedSpaceIds, space.id]);
      return;
    }
    this.selectedSpaceIds = new Set([...this.selectedSpaceIds].filter((id) => id !== space.id));
  }

  onSpaceOptionClick(space: AlgoSpace): void {
    if (this.isSpaceInteractionDisabled(space)) {
      return;
    }
    this.toggleSpace(space);
  }

  getOtherMappedBrokerCount(space: AlgoSpace): number {
    if (this.isSelectOnly || !this.data.brokerDetails) {
      return space.mapped_broker_count ?? 0;
    }
    return Math.max(0, (space.mapped_broker_count ?? 0) - 1);
  }

  copyReservedIp(event: Event, ip: string): void {
    event.stopPropagation();
    this.clipboard.copy(ip);
    this.snackBar.simpleCustomToaster(API_KEY_COPY_SUCCESS);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  redirectToBuyAlgoSpace(): void {
    this.manageBrokerService.redirectToBuyAlgoSpace();
  }

  get isConfirmDisabled(): boolean {
    if (this.isSubmitting) {
      return true;
    }
    if (this.isSelectOnly) {
      return this.data.mandatory === true && this.selectedSpaceIds.size === 0;
    }
    return !this.hasSelectionChanged();
  }

  confirmSelection(): void {
    if (this.isSelectOnly) {
      this.dialogRef.close({ selectedSpaceIds: [...this.selectedSpaceIds] });
      return;
    }
    this.mapSpace();
  }

  mapSpace(): void {
    const spaces = this.getChangedSpaceMappings();
    if (!spaces.length || this.isSubmitting || !this.data.brokerDetails) {
      return;
    }
    for (const mapping of spaces) {
      const space = this.algoSpaces.find((item) => item.id === mapping.space_id);
      if (!space) {
        continue;
      }
      const blockReason = this.getMapBlockReason(space, mapping.mapped);
      if (blockReason) {
        this.snackBar.simpleCustomToaster(blockReason, false);
        return;
      }
    }
    this.isSubmitting = true;
    const { id: connectionId } = this.data.brokerDetails;
    this.manageBrokerService.mapAlgoSpace(connectionId, spaces).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const results = response.data?.results ?? [];
        const failures = results.filter((result) => !result.success);
        const successes = results.filter((result) => result.success);

        failures.forEach((result) => {
          this.snackBar.simpleCustomToaster(result.error || result.message || 'Unable to update algo space mapping.', false);
        });

        if (successes.length) {
          const updatedBroker = this.manageBrokerService.applyMapAlgoSpaceResults(
            this.data.brokerDetails,
            results,
            this.algoSpaces,
          );
          this.snackBar.simpleCustomToaster(response.message || 'Algo space mapping updated.');
          this.fireMixpanelEvent(MIXPANEL_EVENTS.VIRTUAL_ALGO_SPACE_ASSIGNMENT_SUCCESS, {
            from: this.data.from,
            broker: updatedBroker.broker,
            ips_mapped: updatedBroker.algo_spaces?.map((s) => s.reserved_ip) ?? [],
          });
          this.dialogRef.close(updatedBroker);
          return;
        }

        this.dialogRef.close();
      },
      error: (error) => {
        this.isSubmitting = false;
        this.snackBar.simpleCustomToaster(error?.error?.message ?? 'Unable to update algo space mapping.', false);
      },
    });
  }

  fireMixpanelEvent(event: string, params: Record<string, any>) {
    try {
      this.mixpanel.track(event, params);
    } catch (error) { }
  }

  hasSelectionChanged(): boolean {
    if (this.isSelectOnly) {
      return this.selectedSpaceIds.size > 0;
    }
    return this.getChangedSpaceMappings().length > 0;
  }

  private loadAlgoSpaceContext(isRefresh = false): void {
    if (isRefresh) {
      this.isRefreshing = true;
    } else {
      this.isLoading = true;
    }

    const load$ = this.isSelectOnly || !this.data.brokerDetails?.id
      ? forkJoin({
          algoSpaces: this.manageBrokerService.getAlgoSpaces(),
          hasRunningAlgos: of(false),
          connectedBrokers: of(null),
        })
      : forkJoin({
          algoSpaces: this.manageBrokerService.getAlgoSpaces({
            brokerConnectionId: this.data.brokerDetails.id,
          }),
          hasRunningAlgos: this.manageBrokerService.hasRunningAlgosOnBroker(this.data.brokerDetails.broker),
          connectedBrokers: this.manageBrokerService.getConnectedBrokers(true),
        });

    load$.subscribe({
      next: ({ algoSpaces, hasRunningAlgos, connectedBrokers }) => {
        this.algoSpaces = algoSpaces.data ?? [];
        this.connectionMaxSpaceMappings = algoSpaces.max_space_mappings ?? null;
        this.brokerHasRunningAlgos = hasRunningAlgos;
        if (!this.isSelectOnly && this.data.brokerDetails) {
          const freshBroker = connectedBrokers?.data?.find((item) => item.id === this.data.brokerDetails.id)
            ?? this.manageBrokerService.getConnectedBrokerByConnectionId(this.data.brokerDetails.id);
          if (freshBroker) {
            this.data.brokerDetails = freshBroker;
            if (!isRefresh) {
              const mappedIds = this.data.brokerDetails.algo_spaces?.map((space) => space.id)
                ?? [...this.selectedSpaceIds];
              this.selectedSpaceIds = new Set(mappedIds);
            }
          }
        }
        this.isLoading = false;
        this.isRefreshing = false;
      },
      error: () => {
        this.isLoading = false;
        this.isRefreshing = false;
        this.snackBar.simpleCustomToaster('Failed to load algo spaces. Please refresh.', false);
      },
    });
  }

  private getChangedSpaceMappings(): AlgoSpaceMapping[] {
    const initial = new Set(this.data.brokerDetails?.algo_spaces?.map((space) => space.id) ?? []);
    const allIds = new Set([...initial, ...this.selectedSpaceIds]);
    return [...allIds]
      .filter((spaceId) => initial.has(spaceId) !== this.selectedSpaceIds.has(spaceId))
      .map((spaceId) => ({
        space_id: spaceId,
        mapped: this.selectedSpaceIds.has(spaceId),
      }));
  }

  trackBySpaceId(_: number, space: AlgoSpace): string {
    return space.id;
  }
}
